import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

export type SearchParamsValuesType = {
  [key: string]: number | string | null | undefined | string[];
};

export type SetUrlParamsOptionsType = {
  isPartialUpdate?: boolean;
  replace?: boolean;
};

export type SetParamsFunctionType<TParams> = (
  values: Partial<TParams>,
  updateOptions?: SetUrlParamsOptionsType
) => void;

export type SetUrlParamsResponseType<TParams> = [
  Partial<Record<keyof TParams, string>>,
  SetParamsFunctionType<TParams>,
];

export const DEFAULT_UPDATE_OPTIONS = {
  isPartialUpdate: false,
  replace: false,
};

export const useSetUrlParams = <
  TParams extends SearchParamsValuesType,
>(): SetUrlParamsResponseType<TParams> => {
  const navigate = useNavigate();

  const location = useLocation();
  const [currentSearchParams, currentSearchParamsObj] = useMemo(() => {
    const searchParams = new URLSearchParams(location.search.substring(1));
    const values = {};

    for (const [key, value] of searchParams.entries()) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      if (values[key]) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        values[key]?.push(value);
      } else {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        values[key] = [value];
      }
    }

    return [searchParams, values];
  }, [location.search]);

  const setUrlParams: SetParamsFunctionType<TParams> = useCallback(
    (values, updateOptions = DEFAULT_UPDATE_OPTIONS): void => {
      const { replace, isPartialUpdate } = {
        ...DEFAULT_UPDATE_OPTIONS,
        ...updateOptions,
      };

      const newSearchParams = new URLSearchParams(
        isPartialUpdate ? currentSearchParams : undefined
      );

      Object.keys(values).forEach((key) => {
        const value = values[key];
        if (Array.isArray(value)) {
          value.forEach((v) => {
            newSearchParams.append(key, v);
          });
        } else if (value) {
          newSearchParams.set(key, value.toString());
        } else {
          newSearchParams.delete(key);
        }
      });

      // Carry the current router state over, otherwise navigating only to
      // change the query string would drop any details the previous page
      // forwarded through location.state.
      navigate(
        { search: `?${newSearchParams.toString()}` },
        { replace, state: location.state }
      );
    },
    [currentSearchParams, location.state, navigate]
  );

  return [currentSearchParamsObj, setUrlParams];
};
